import { useState, useEffect, useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import authService, { TOKEN_TYPES } from "../services/authService";
import {
  superuserService,
  SUPERUSER_TOKEN_KEY,
  IMPERSONATED_USER_KEY,
} from "../services/superuserService";
import type {
  ImpersonatedUser,
  SuperuserContextValue,
} from "./superuser.types";
import { SuperuserContext } from "./SuperuserContextDef";

export const SuperuserProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isSuperuserMode, setIsSuperuserMode] = useState(false);
  // Restore the impersonated user from sessionStorage on first render so an
  // active impersonation survives a page refresh (the impersonation access
  // token and the stashed superuser token both live in sessionStorage). This
  // initialises synchronously so the banner shows immediately rather than
  // flickering through the superuser's identity.
  const [impersonatedUser, setImpersonatedUser] =
    useState<ImpersonatedUser | null>(() => {
      const stored = sessionStorage.getItem(IMPERSONATED_USER_KEY);
      if (!stored) return null;
      try {
        return JSON.parse(stored) as ImpersonatedUser;
      } catch {
        sessionStorage.removeItem(IMPERSONATED_USER_KEY);
        return null;
      }
    });
  // Role impersonation keeps the superuser's identity, so the active claim
  // lives in the access token itself. Initialise from the current token so an
  // active role-impersonation session survives a page refresh — the refreshed
  // token (issued by IAM) keeps carrying the impersonated_role claim.
  const [impersonatedRole, setImpersonatedRole] = useState<string | null>(
    () => {
      const token = authService.getToken(TOKEN_TYPES.ACCESS);
      if (!token) return null;
      try {
        return (
          ((authService.decodeToken(token) as Record<string, unknown>)
            .impersonated_role as string) ?? null
        );
      } catch {
        return null;
      }
    },
  );
  const queryClient = useQueryClient();

  // When the page is refreshed during impersonation, the impersonation access
  // token is still in storage, but useAuthService re-arms the token refresh
  // and tab-visibility listener against it on boot. The impersonation token
  // must never be refreshed — the refresh token still belongs to the
  // superuser, so a refresh would silently revert to the superuser.
  useEffect(() => {
    if (!impersonatedUser) return;

    authService.cancelRefresh();
    authService.removeVisibilityChangeListener();
  }, [impersonatedUser]);

  const startImpersonation = useCallback(
    async (userId: string): Promise<void> => {
      const currentToken = authService.getToken(TOKEN_TYPES.ACCESS);
      if (!currentToken) throw new Error("No active session");

      const data = await superuserService.impersonateUser(userId);

      // Disarm the refresh timer and visibility listener BEFORE writing any
      // storage keys. This ensures isImpersonating() never becomes true while a
      // live timer is still armed, which would leave the timer no-oping on the
      // superuser's own (soon-to-be-stashed) token.
      authService.cancelRefresh();
      authService.removeVisibilityChangeListener();

      sessionStorage.setItem(SUPERUSER_TOKEN_KEY, currentToken);
      sessionStorage.setItem(
        IMPERSONATED_USER_KEY,
        JSON.stringify(data.impersonated_user),
      );
      authService.setToken(TOKEN_TYPES.ACCESS, data.access_token);

      // Refetch userInfo BEFORE removing it from the cache. clear() removes the
      // query registration so a subsequent refetchQueries finds nothing; instead
      // we force-refetch the active query first, then wipe all other stale data.
      await queryClient.refetchQueries({
        queryKey: ["userInfo"],
        type: "active",
      });
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== "userInfo",
      });

      setImpersonatedUser(data.impersonated_user);
    },
    [queryClient],
  );

  const stopImpersonation = useCallback(async (): Promise<void> => {
    const superuserToken = sessionStorage.getItem(SUPERUSER_TOKEN_KEY);
    if (!superuserToken) {
      // The stashed superuser token is gone (e.g. storage was cleared).
      // Log the user out rather than leave them in an inconsistent auth state.
      authService.logout(); // logout calls endImpersonation() internally
      return;
    }

    authService.setToken(TOKEN_TYPES.ACCESS, superuserToken);
    authService.endImpersonation();

    // Resume the token refresh cycle and tab-visibility listener for the
    // superuser's own session.
    authService.scheduleTokenRefresh();
    authService.initVisibilityChangeListener();

    await queryClient.refetchQueries({
      queryKey: ["userInfo"],
      type: "active",
    });
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== "userInfo",
    });

    setImpersonatedUser(null);
  }, [queryClient]);

  // Swap to a freshly-issued token, then re-derive permission-gated UI state.
  // Token refresh stays enabled (identity is unchanged) so we re-arm the
  // refresh timer against the new token's expiry rather than cancelling it.
  const applyRoleToken = useCallback(
    async (accessToken: string, role: string | null): Promise<void> => {
      authService.setToken(TOKEN_TYPES.ACCESS, accessToken);
      authService.scheduleTokenRefresh();

      // Refetch userInfo BEFORE wiping the rest of the cache so the active query
      // re-runs against the new grants; then drop all other now-stale data.
      await queryClient.refetchQueries({
        queryKey: ["userInfo"],
        type: "active",
      });
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== "userInfo",
      });

      setImpersonatedRole(role);
    },
    [queryClient],
  );

  const startRoleImpersonation = useCallback(
    async (roleSlug: string): Promise<void> => {
      const data = await superuserService.impersonateRole(roleSlug);
      await applyRoleToken(
        data.access_token,
        data.impersonated_role ?? roleSlug,
      );
    },
    [applyRoleToken],
  );

  const stopRoleImpersonation = useCallback(async (): Promise<void> => {
    const data = await superuserService.endRoleImpersonation();
    await applyRoleToken(data.access_token, null);
  }, [applyRoleToken]);

  const value = useMemo<SuperuserContextValue>(
    () => ({
      isSuperuserMode,
      setIsSuperuserMode,
      impersonatedUser,
      startImpersonation,
      stopImpersonation,
      impersonatedRole,
      startRoleImpersonation,
      stopRoleImpersonation,
    }),
    [
      isSuperuserMode,
      impersonatedUser,
      startImpersonation,
      stopImpersonation,
      impersonatedRole,
      startRoleImpersonation,
      stopRoleImpersonation,
    ],
  );

  return (
    <SuperuserContext.Provider value={value}>
      {children}
    </SuperuserContext.Provider>
  );
};
